// CMS B2B Orders Management
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useCMS } from './CMSContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Building2, Plus, FileText, CreditCard, Check, Eye, Download, Search, RefreshCw, Printer, BarChart3, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PASS_TYPES = [
  { value: 'one_time', label: 'One-Time Pass', defaultPrice: 28.00 },
  { value: 'annual', label: 'Annual Pass', defaultPrice: 99.00 },
  { value: 'free_annual', label: 'Free Annual Pass', defaultPrice: 0.00 }
];

const STATUSES = {
  pending: { label: 'Pending', color: 'bg-yellow-500' },
  confirmed: { label: 'Confirmed', color: 'bg-cyan-500' },
  quote_requested: { label: 'Quote', color: 'bg-purple-500' },
  invoiced: { label: 'Invoiced', color: 'bg-blue-500' },
  paid: { label: 'Paid', color: 'bg-green-500' },
  cancelled: { label: 'Cancelled', color: 'bg-red-500' }
};

const CMSB2B = () => {
  const { token, hasRole, cmsTheme } = useCMS();
  const outletContext = useOutletContext();
  const theme = cmsTheme || outletContext?.cmsTheme || 'dark';
  
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  const [editOrder, setEditOrder] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [managerFilter, setManagerFilter] = useState('');
  const [managers, setManagers] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [formData, setFormData] = useState({
    business_name: '',
    business_address: '',
    business_email: '',
    business_phone: '',
    vat_number: '',
    pass_type: 'one_time',
    quantity: 1,
    unit_price: 28.00,
    notes: ''
  });

  // Theme-aware classes
  const tc = {
    bg: theme === 'dark' ? 'bg-slate-900' : 'bg-gray-50',
    card: theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200',
    text: theme === 'dark' ? 'text-white' : 'text-gray-900',
    textMuted: theme === 'dark' ? 'text-slate-400' : 'text-gray-500',
    input: theme === 'dark' ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900',
    tableHead: theme === 'dark' ? 'text-slate-300' : 'text-gray-600',
    tableRow: theme === 'dark' ? 'border-slate-700' : 'border-gray-200',
    tableCell: theme === 'dark' ? 'text-slate-300' : 'text-gray-700',
  };

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [page, statusFilter, managerFilter]);

  const fetchOrders = async () => {
    try {
      let url = `${API}/cms/b2b/orders?page=${page}&limit=20`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (managerFilter) url += `&manager=${encodeURIComponent(managerFilter)}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setOrders(data.orders || []);
      setTotal(data.total || 0);
      if (data.managers) setManagers(data.managers);
    } catch (error) {
      toast.error('Failed to load B2B orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/cms/b2b/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to load B2B stats');
    }
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/cms/b2b/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('B2B order created');
        setIsCreateOpen(false);
        resetForm();
        fetchOrders();
        fetchStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create order');
      }
    } catch (error) {
      toast.error('Failed to create order');
    }
  };

  const handleGenerateInvoice = async (orderId) => {
    try {
      const res = await fetch(`${API}/cms/b2b/orders/${orderId}/generate-invoice`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Invoice ${data.invoice_number} aangemaakt en verzonden per email`);
        fetchOrders();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Factuur kon niet worden verzonden. Status blijft ongewijzigd.');
      }
    } catch (error) {
      toast.error('Factuur kon niet worden verzonden. Controleer SMTP instellingen.');
    }
  };

  const handleMarkPaid = async (orderId) => {
    if (!window.confirm('Order als betaald markeren en passes genereren?')) return;
    
    try {
      const res = await fetch(`${API}/cms/b2b/orders/${orderId}/mark-paid`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        if (data.email_sent) {
          toast.success(`${data.passes_generated} passes gegenereerd en verzonden per email!`);
        } else {
          toast.success(`${data.passes_generated} passes gegenereerd! Let op: email kon niet worden verzonden.`);
        }
        fetchOrders();
        fetchStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Kon niet als betaald markeren');
      }
    } catch (error) {
      toast.error('Kon niet als betaald markeren');
    }
  };

  const handleViewOrder = async (orderId) => {
    try {
      const res = await fetch(`${API}/cms/b2b/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setViewOrder(data);
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };

  // Open edit dialog with order data
  const handleEditOrder = (order) => {
    setEditOrder({
      ...order,
      unit_price: order.unit_price ?? (order.pass_type === 'annual' ? 99.00 : order.pass_type === 'free_annual' ? 0.00 : 28.00)
    });
    setIsEditOpen(true);
  };

  // Save edited order
  const handleSaveEdit = async () => {
    if (!editOrder) return;
    
    try {
      const res = await fetch(`${API}/cms/b2b/orders/${editOrder.order_id}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          business_name: editOrder.business_name,
          business_address: editOrder.business_address,
          business_email: editOrder.business_email,
          business_phone: editOrder.business_phone,
          vat_number: editOrder.vat_number,
          pass_type: editOrder.pass_type,
          quantity: editOrder.quantity,
          unit_price: editOrder.unit_price,
          total_price: editOrder.unit_price * editOrder.quantity,
          status: editOrder.status,
          notes: editOrder.notes
        })
      });
      
      if (res.ok) {
        toast.success('Order updated successfully');
        setIsEditOpen(false);
        setEditOrder(null);
        fetchOrders();
        fetchStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update order');
      }
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  // Delete order (only pending orders)
  const handleDeleteOrder = async (orderId) => {
    if (!confirm('Are you sure you want to delete this order?')) return;
    
    try {
      const res = await fetch(`${API}/cms/b2b/orders/${orderId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Order deleted');
        fetchOrders();
        fetchStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete order');
      }
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  // Print invoice for a specific order
  const handlePrintInvoice = (order) => {
    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${order.invoice_number || 'Draft'}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
          .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
          .logo { font-size: 24px; font-weight: bold; color: #1a73e8; }
          .invoice-title { font-size: 32px; color: #666; }
          .info-section { margin-bottom: 30px; }
          .info-section h3 { color: #1a73e8; border-bottom: 2px solid #1a73e8; padding-bottom: 5px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background: #f5f5f5; }
          .total-row { font-weight: bold; font-size: 18px; background: #e8f0fe; }
          .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; }
          @media print { body { margin: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">Freestays</div>
          <div class="invoice-title">INVOICE</div>
        </div>
        <div class="info-grid">
          <div class="info-section">
            <h3>Bill To</h3>
            <p><strong>${order.business_name}</strong></p>
            <p>${order.business_address}</p>
            <p>${order.business_email}</p>
            <p>${order.business_phone}</p>
            <p>VAT: ${order.vat_number}</p>
          </div>
          <div class="info-section">
            <h3>Invoice Details</h3>
            <p><strong>Invoice #:</strong> ${order.invoice_number || 'N/A'}</p>
            <p><strong>Order ID:</strong> ${order.order_id}</p>
            <p><strong>Date:</strong> ${new Date(order.created_at).toLocaleDateString()}</p>
            <p><strong>Status:</strong> ${order.status?.toUpperCase()}</p>
            ${order.manager_name ? `<p><strong>Sales Manager:</strong> ${order.manager_name}</p>` : ''}
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Type</th>
              <th>Qty</th>
              <th>Stukprijs excl. BTW</th>
              <th>Subtotaal</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Freestays Pass</td>
              <td>${order.pass_type === 'annual' ? 'Annual Pass' : order.pass_type === 'free_annual' ? 'Free Annual Pass' : 'One-Time Pass'}</td>
              <td>${order.quantity}</td>
              <td>&euro;${order.unit_price?.toFixed(2)}</td>
              <td>&euro;${order.total_price?.toFixed(2)}</td>
            </tr>
            <tr>
              <td colspan="4" style="text-align: right;">Subtotaal excl. BTW</td>
              <td>&euro;${order.total_price?.toFixed(2)}</td>
            </tr>
            <tr>
              <td colspan="4" style="text-align: right;">BTW 21%</td>
              <td>&euro;${(order.btw_amount || (order.total_price * 0.21)).toFixed(2)}</td>
            </tr>
            <tr class="total-row">
              <td colspan="4" style="text-align: right;">Totaal incl. BTW</td>
              <td>&euro;${(order.total_price_incl_btw || (order.total_price * 1.21)).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
        ${order.pass_codes?.length ? `
          <div class="info-section">
            <h3>Pass Codes</h3>
            <p>${order.pass_codes.join(', ')}</p>
          </div>
        ` : ''}
        ${order.notes ? `<p><strong>Notes:</strong> ${order.notes}</p>` : ''}
        <div class="footer">
          <p>Freestays - Premium Hotel Booking Platform</p>
          <p>www.freestays.eu | administration@freestays.eu</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  // Print statistics report
  const handlePrintStats = () => {
    if (!stats) return;
    
    const statsHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>B2B Statistics Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
          .header { text-align: center; margin-bottom: 40px; }
          .logo { font-size: 24px; font-weight: bold; color: #1a73e8; }
          .title { font-size: 28px; margin: 10px 0; }
          .date { color: #666; }
          .stats-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin: 30px 0; }
          .stat-card { background: #f8f9fa; padding: 20px; border-radius: 8px; text-align: center; }
          .stat-value { font-size: 32px; font-weight: bold; color: #1a73e8; }
          .stat-label { color: #666; margin-top: 5px; }
          .orders-table { width: 100%; border-collapse: collapse; margin-top: 30px; }
          .orders-table th, .orders-table td { padding: 10px; border: 1px solid #ddd; text-align: left; }
          .orders-table th { background: #1a73e8; color: white; }
          .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; }
          @media print { body { margin: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">Freestays</div>
          <div class="title">B2B Statistics Report</div>
          <div class="date">Generated: ${new Date().toLocaleString()}</div>
        </div>
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-value">${stats.total_orders || 0}</div>
            <div class="stat-label">Total Orders</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${stats.pending_orders || 0}</div>
            <div class="stat-label">Pending Orders</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">€${stats.total_revenue?.toFixed(2) || '0.00'}</div>
            <div class="stat-label">Total Revenue</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${stats.total_passes_sold || 0}</div>
            <div class="stat-label">Passes Sold</div>
          </div>
        </div>
        <h3>Recent Orders</h3>
        <table class="orders-table">
          <thead>
            <tr>
              <th>Business</th>
              <th>Pass Type</th>
              <th>Qty</th>
              <th>Total</th>
              <th>Status</th>
              <th>Manager</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            ${orders.map(o => `
              <tr>
                <td>${o.business_name}</td>
                <td>${o.pass_type}</td>
                <td>${o.quantity}</td>
                <td>€${o.total_price?.toFixed(2)}</td>
                <td>${o.status}</td>
                <td>${o.manager_name || '-'}</td>
                <td>${new Date(o.created_at).toLocaleDateString()}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="footer">
          <p>Freestays - Premium Hotel Booking Platform</p>
          <p>www.freestays.eu | administration@freestays.eu</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(statsHTML);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  // Export stats as CSV
  const handleExportStats = () => {
    const headers = ['Business Name', 'Email', 'VAT', 'Pass Type', 'Quantity', 'Stukprijs excl. BTW', 'Subtotaal', 'BTW 21%', 'Totaal incl. BTW', 'Status', 'Invoice #', 'Manager', 'Manager Email', 'Created'];
    const rows = orders.map(o => [
      o.business_name,
      o.business_email,
      o.vat_number,
      o.pass_type,
      o.quantity,
      o.unit_price,
      o.total_price,
      (o.btw_amount || (o.total_price * 0.21)).toFixed(2),
      (o.total_price_incl_btw || (o.total_price * 1.21)).toFixed(2),
      o.status,
      o.invoice_number || '',
      o.manager_name || '',
      o.manager_email || '',
      o.created_at
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `b2b_orders_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setFormData({
      business_name: '',
      business_address: '',
      business_email: '',
      business_phone: '',
      vat_number: '',
      pass_type: 'one_time',
      quantity: 1,
      unit_price: 28.00,
      notes: ''
    });
  };

  const updatePassType = (type) => {
    const passInfo = PASS_TYPES.find(p => p.value === type);
    setFormData({ 
      ...formData, 
      pass_type: type, 
      unit_price: passInfo?.defaultPrice ?? 0 
    });
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cms-b2b">
      <div className="flex justify-between items-center">
        <div>
          <h1 className={`text-2xl font-bold ${tc.text} flex items-center gap-2`}>
            <Building2 className="h-6 w-6" /> B2B Pass Management
          </h1>
          <p className={tc.textMuted}>Manage business pass orders and invoices</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrintStats} title="Print Statistics">
            <BarChart3 className="h-4 w-4 mr-2" /> Print Stats
          </Button>
          <Button variant="outline" onClick={handleExportStats} title="Export CSV">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-b2b-order-btn">
                <Plus className="h-4 w-4 mr-2" /> Create B2B Order
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-gray-900">Create B2B Pass Order</DialogTitle>
              </DialogHeader>
            <form onSubmit={handleCreateOrder} className="space-y-4">
              <div>
                <Label className="text-gray-700">Business Name *</Label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  className="!bg-white !border-gray-300 !text-gray-900 placeholder:text-gray-400"
                  required
                />
              </div>
              <div>
                <Label className="text-gray-700">Business Address *</Label>
                <Textarea
                  value={formData.business_address}
                  onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                  className="!bg-white !border-gray-300 !text-gray-900 placeholder:text-gray-400"
                  rows={2}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Email *</Label>
                  <Input
                    type="email"
                    value={formData.business_email}
                    onChange={(e) => setFormData({ ...formData, business_email: e.target.value })}
                    className="!bg-white !border-gray-300 !text-gray-900 placeholder:text-gray-400"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Phone *</Label>
                  <Input
                    value={formData.business_phone}
                    onChange={(e) => setFormData({ ...formData, business_phone: e.target.value })}
                    className="!bg-white !border-gray-300 !text-gray-900 placeholder:text-gray-400"
                    required
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-700">VAT Number *</Label>
                <Input
                  value={formData.vat_number}
                  onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                  className="!bg-white !border-gray-300 !text-gray-900 placeholder:text-gray-400"
                  placeholder="e.g., NL123456789B01"
                  required
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-700">Pass Type</Label>
                  <Select value={formData.pass_type} onValueChange={updatePassType}>
                    <SelectTrigger className="!bg-white !border-gray-300 !text-gray-900">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PASS_TYPES.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700">Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                    className="!bg-white !border-gray-300 !text-gray-900 placeholder:text-gray-400"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Stukprijs excl. BTW (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.unit_price}
                    onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })}
                    className="!bg-white !border-gray-300 !text-gray-900 placeholder:text-gray-400"
                  />
                </div>
              </div>
              <div className="bg-gray-100 p-3 rounded-lg space-y-1">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotaal ({formData.quantity}× €{(formData.unit_price ?? 0).toFixed(2)})</span>
                  <span>€{(formData.unit_price * formData.quantity).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>BTW 21%</span>
                  <span>€{(Math.round(formData.unit_price * formData.quantity * 0.21 * 100) / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-gray-900 pt-1 border-t border-gray-300">
                  <span>Totaal incl. BTW</span>
                  <span>€{(Math.round(formData.unit_price * formData.quantity * 1.21 * 100) / 100).toFixed(2)}</span>
                </div>
              </div>
              <div>
                <Label className="text-gray-700">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="!bg-white !border-gray-300 !text-gray-900 placeholder:text-gray-400"
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                <Button type="submit">Create Order</Button>
              </div>
            </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={tc.card}>
            <CardContent className="p-4">
              <p className={`text-sm ${tc.textMuted}`}>Total Orders</p>
              <p className={`text-2xl font-bold ${tc.text}`}>{stats.total_orders}</p>
            </CardContent>
          </Card>
          <Card className={`${theme === 'dark' ? 'bg-yellow-900/30 border-yellow-700' : 'bg-yellow-50 border-yellow-200'}`}>
            <CardContent className="p-4">
              <p className={`text-sm ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`}>Pending</p>
              <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`}>{stats.pending_orders}</p>
            </CardContent>
          </Card>
          <Card className={`${theme === 'dark' ? 'bg-green-900/30 border-green-700' : 'bg-green-50 border-green-200'}`}>
            <CardContent className="p-4">
              <p className={`text-sm ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>Revenue</p>
              <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>€{stats.total_revenue?.toFixed(2)}</p>
            </CardContent>
          </Card>
          <Card className={`${theme === 'dark' ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
            <CardContent className="p-4">
              <p className={`text-sm ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>Passes Sold</p>
              <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>{stats.total_passes_sold}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className={tc.card}>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search business name, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`${tc.input}`}
                onKeyDown={(e) => e.key === 'Enter' && fetchOrders()}
              />
            </div>
            <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className={`w-40 ${tc.input}`}>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="invoiced">Invoiced</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={managerFilter || "all"} onValueChange={(v) => { setManagerFilter(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className={`w-48 ${tc.input}`}>
                <SelectValue placeholder="Manager" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Managers</SelectItem>
                {managers.map(m => (
                  <SelectItem key={m.email} value={m.email}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchOrders}>
              <Search className="h-4 w-4 mr-2" /> Search
            </Button>
            <Button variant="ghost" onClick={() => { fetchOrders(); fetchStats(); }}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card className={tc.card}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className={tc.tableRow}>
                <TableHead className={tc.tableHead}>Business</TableHead>
                <TableHead className={tc.tableHead}>Pass Type</TableHead>
                <TableHead className={tc.tableHead}>Qty</TableHead>
                <TableHead className={tc.tableHead}>Total</TableHead>
                <TableHead className={tc.tableHead}>Invoice</TableHead>
                <TableHead className={tc.tableHead}>Status</TableHead>
                <TableHead className={tc.tableHead}>Manager</TableHead>
                <TableHead className={tc.tableHead}>Created</TableHead>
                <TableHead className={tc.tableHead}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow className={tc.tableRow}>
                  <TableCell colSpan={9} className={`text-center py-8 ${tc.textMuted}`}>
                    No B2B orders found
                  </TableCell>
                </TableRow>
              ) : (
                orders.map(order => (
                  <TableRow key={order.order_id} className={tc.tableRow}>
                    <TableCell>
                      <div>
                        <p className={`font-medium ${tc.text}`}>{order.business_name}</p>
                        <p className={`text-xs ${tc.textMuted}`}>{order.business_email}</p>
                      </div>
                    </TableCell>
                    <TableCell className={tc.tableCell}>{PASS_TYPES.find(p => p.value === order.pass_type)?.label || order.pass_type}</TableCell>
                    <TableCell className={tc.tableCell}>{order.quantity}</TableCell>
                    <TableCell className="text-green-500 font-medium">€{order.total_price?.toFixed(2)}</TableCell>
                    <TableCell className={tc.tableCell}>{order.invoice_number || '-'}</TableCell>
                    <TableCell>
                      <Badge className={`${STATUSES[order.status]?.color || 'bg-gray-500'} text-white`}>
                        {STATUSES[order.status]?.label || order.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className={`text-sm ${tc.text}`}>{order.manager_name || '-'}</p>
                        {order.manager_email && <p className={`text-xs ${tc.textMuted}`}>{order.manager_email}</p>}
                      </div>
                    </TableCell>
                    <TableCell className={tc.textMuted}>{formatDate(order.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleViewOrder(order.order_id)} title="View Details">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleEditOrder(order)} title="Edit Order">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {order.invoice_number && (
                          <Button size="sm" variant="ghost" onClick={() => handlePrintInvoice(order)} title="Print Invoice">
                            <Printer className="h-4 w-4" />
                          </Button>
                        )}
                        {order.status === 'pending' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleGenerateInvoice(order.order_id)} title="Factuur versturen naar klant" data-testid={`generate-invoice-${order.order_id}`}>
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600" onClick={() => handleDeleteOrder(order.order_id)} title="Delete Order">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {order.status === 'invoiced' && (
                          <Button size="sm" variant="outline" className="text-green-500 hover:text-green-600" onClick={() => handleMarkPaid(order.order_id)} title="Mark as Paid">
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Order Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Edit B2B Order</DialogTitle>
          </DialogHeader>
          {editOrder && (
            <div className="space-y-4">
              <div>
                <Label className="text-gray-700">Business Name</Label>
                <Input
                  value={editOrder.business_name}
                  onChange={(e) => setEditOrder({...editOrder, business_name: e.target.value})}
                  className="!bg-white !border-gray-300 !text-gray-900 placeholder:text-gray-400"
                />
              </div>
              <div>
                <Label className="text-gray-700">Business Address</Label>
                <Textarea
                  value={editOrder.business_address}
                  onChange={(e) => setEditOrder({...editOrder, business_address: e.target.value})}
                  className="!bg-white !border-gray-300 !text-gray-900 placeholder:text-gray-400"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Email</Label>
                  <Input
                    type="email"
                    value={editOrder.business_email}
                    onChange={(e) => setEditOrder({...editOrder, business_email: e.target.value})}
                    className="!bg-white !border-gray-300 !text-gray-900 placeholder:text-gray-400"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Phone</Label>
                  <Input
                    value={editOrder.business_phone}
                    onChange={(e) => setEditOrder({...editOrder, business_phone: e.target.value})}
                    className="!bg-white !border-gray-300 !text-gray-900 placeholder:text-gray-400"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-700">VAT Number</Label>
                <Input
                  value={editOrder.vat_number}
                  onChange={(e) => setEditOrder({...editOrder, vat_number: e.target.value})}
                  className="!bg-white !border-gray-300 !text-gray-900 placeholder:text-gray-400"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-700">Pass Type</Label>
                  <Select 
                    value={editOrder.pass_type} 
                    onValueChange={(v) => {
                      const passInfo = PASS_TYPES.find(p => p.value === v);
                      setEditOrder({
                        ...editOrder, 
                        pass_type: v, 
                        unit_price: passInfo?.defaultPrice ?? 0
                      });
                    }}
                  >
                    <SelectTrigger className="!bg-white !border-gray-300 !text-gray-900">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PASS_TYPES.map(pt => (
                        <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700">Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editOrder.quantity}
                    onChange={(e) => setEditOrder({...editOrder, quantity: parseInt(e.target.value) || 1})}
                    className="!bg-white !border-gray-300 !text-gray-900 placeholder:text-gray-400"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Unit Price (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editOrder.unit_price}
                    onChange={(e) => setEditOrder({...editOrder, unit_price: parseFloat(e.target.value) || 0})}
                    className="!bg-white !border-gray-300 !text-gray-900 placeholder:text-gray-400"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-700">Status</Label>
                <Select 
                  value={editOrder.status} 
                  onValueChange={(v) => setEditOrder({...editOrder, status: v})}
                >
                  <SelectTrigger className="!bg-white !border-gray-300 !text-gray-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="invoiced">Invoiced</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-gray-100 p-3 rounded-lg">
                <p className="text-sm text-gray-600">
                  Total: <span className="text-xl font-bold text-gray-900">€{(editOrder.unit_price * editOrder.quantity).toFixed(2)}</span>
                </p>
              </div>
              <div>
                <Label className="text-gray-700">Notes</Label>
                <Textarea
                  value={editOrder.notes || ''}
                  onChange={(e) => setEditOrder({...editOrder, notes: e.target.value})}
                  className="!bg-white !border-gray-300 !text-gray-900 placeholder:text-gray-400"
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => { setIsEditOpen(false); setEditOrder(null); }}>Cancel</Button>
                <Button onClick={handleSaveEdit}>Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pagination */}
      {total > 20 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className={`flex items-center px-4 ${tc.textMuted}`}>Page {page} of {Math.ceil(total / 20)}</span>
          <Button variant="outline" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      {/* View Order Dialog */}
      <Dialog open={!!viewOrder} onOpenChange={() => setViewOrder(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-gray-900">B2B Order Details</DialogTitle>
          </DialogHeader>
          {viewOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Business Name</p>
                  <p className="font-medium">{viewOrder.business_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">VAT Number</p>
                  <p className="font-medium">{viewOrder.vat_number}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{viewOrder.business_email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{viewOrder.business_phone}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="font-medium">{viewOrder.business_address}</p>
                </div>
              </div>
              <div className="border-t pt-4">
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Pass Type</p>
                    <p className="font-medium">{PASS_TYPES.find(p => p.value === viewOrder.pass_type)?.label || viewOrder.pass_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Quantity</p>
                    <p className="font-medium">{viewOrder.quantity}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Unit Price</p>
                    <p className="font-medium">€{viewOrder.unit_price?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total</p>
                    <p className="font-medium text-green-600">€{viewOrder.total_price?.toFixed(2)}</p>
                  </div>
                </div>
              </div>
              {viewOrder.invoice_number && (
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-500">Invoice Number</p>
                  <p className="font-medium text-blue-600">{viewOrder.invoice_number}</p>
                </div>
              )}
              {viewOrder.pass_codes_detail && viewOrder.pass_codes_detail.length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-500 mb-2">Generated Pass Codes</p>
                  <div className="bg-gray-100 p-3 rounded-lg max-h-40 overflow-y-auto">
                    {viewOrder.pass_codes_detail.map((pass, i) => (
                      <div key={i} className="flex justify-between items-center py-1 border-b border-gray-200 last:border-0">
                        <code className="text-sm font-mono">{pass.code}</code>
                        <Badge className={pass.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}>{pass.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSB2B;
